import {
    fetchUpcoming,
    fetchArchived,
    fetchAreas,
    updateStatus as repoUpdateStatus,
    updateReservation as repoUpdateReservation,
} from '../infrastructure/reservations.repository.js';
import { groupByDay, summarizeAreas } from '../shared/admin-reservations-policy.js';
import { mesasDeReserva } from '../shared/mesas-policy.js';
import { CONFIG } from '../config.js';
import { safeFetchClient } from '../utils/safe-fetch-client.js';
import { PERSONAS_MIN, PERSONAS_MAX } from '../shared/constants.js';

/**
 * Servicio de reservas para el panel de staff (Fase 1, slice 2). Orquesta el
 * repositorio (I/O) y la política pura (`admin-reservations-policy.js`); no
 * contiene lógica de agrupación/ocupación propia y no conoce el SDK de
 * Supabase — eso vive en `reservations.repository.js`.
 */

/**
 * Fecha local de hoy en formato ISO `YYYY-MM-DD`. Se usa `getFullYear` /
 * `getMonth` / `getDate` (hora local) en lugar de `toISOString()` (UTC),
 * porque el default del filtro es explícitamente "fecha local de hoy"
 * (Req: Default Window and Date Picker) y `toISOString()` puede adelantar o
 * atrasar un día según la zona horaria del navegador.
 * @param {Date} [now] — injectable para tests (default `new Date()`)
 * @returns {string}
 */
function todayLocalISO(now = new Date()) {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * @typedef {{ area: string, ocupadas: number, capacidad: number, ratio: number }} AreaSummaryViewModel
 * @typedef {{ fecha: string, rows: Array<object>, areas: AreaSummaryViewModel[] }} DayGroupViewModel
 * @typedef {{ groups: DayGroupViewModel[], isEmpty: boolean }} UpcomingViewModel
 */

/**
 * Trae las reservas próximas y las capacidades por área, y compone el modelo
 * de vista día-por-día (grupos por `fecha` ascendente, cada uno con su
 * resumen de ocupación por área) delegando toda la lógica de agrupación/
 * ocupación a `admin-reservations-policy.js` (`groupByDay` + `summarizeAreas`).
 *
 * @param {{ from?: string }} [params] fecha ISO (YYYY-MM-DD), límite inferior
 *   inclusive. Si se omite, usa la fecha local de hoy (Req: Default Window).
 * @returns {Promise<UpcomingViewModel>}
 * @throws {Error} si el repositorio falla (fetch de reservas o de áreas)
 */
export async function loadUpcoming({ from } = {}) {
    const effectiveFrom = from ?? todayLocalISO();

    const [rows, areas] = await Promise.all([fetchUpcoming({ from: effectiveFrom }), fetchAreas()]);

    const groups = groupByDay(rows.map(conMesas)).map((day) => ({
        ...day,
        areas: summarizeAreas(day.rows, areas),
    }));

    return { groups, isEmpty: groups.length === 0 };
}

/**
 * Aplana las mesas asignadas que vienen en el join anidado y las cuelga de la
 * fila, para que la lista pueda mostrarlas sin conocer la forma del join.
 * @param {object} row
 * @returns {object}
 */
function conMesas(row) {
    return { ...row, mesas: mesasDeReserva(row) };
}

/**
 * @typedef {{ rows: Array<object> }} ArchivedViewModel
 */

/**
 * Trae las reservas archivadas como una lista PLANA (no agrupada por día),
 * ya ordenada por fecha descendente desde el repositorio (Req: Archived
 * Reservations View). A diferencia de `loadUpcoming`, no compone grupos ni
 * resumen de ocupación — la vista de archivadas es de solo-lectura.
 * @returns {Promise<ArchivedViewModel>}
 * @throws {Error} si el repositorio falla
 */
export async function loadArchived() {
    const rows = await fetchArchived();
    return { rows: rows.map(conMesas) };
}

/**
 * @typedef {'confirmar' | 'cancelar' | 'terminar' | 'archivar'} ReservationAction
 * @typedef {{ to: import('../infrastructure/reservations.repository.js').ReservationStatus, from: import('../infrastructure/reservations.repository.js').ReservationStatus[] }} ActionTransition
 */

/**
 * Staff transition actions mapped to their target status (`to`) and allowed
 * source statuses (`from`), per the reservation status state machine
 * (Req: Staff Status Transition Actions / Archive Action Restricted to
 * Cancelada or Terminada). Las 3 transiciones de estado son LIBRES entre los 3
 * estados activos (confirmada/cancelada/terminada); `archivar` solo desde
 * cancelada/terminada (el `.in('status', from)` del repo server-guardea esto).
 * This is data, not code — `transition()` below is the single conditional-update
 * path shared by all 4 actions.
 * @type {Record<ReservationAction, ActionTransition>}
 */
export const ACTIONS = {
    confirmar: { to: 'confirmada', from: ['confirmada', 'cancelada', 'terminada'] },
    cancelar: { to: 'cancelada', from: ['confirmada', 'cancelada', 'terminada'] },
    terminar: { to: 'terminada', from: ['confirmada', 'cancelada', 'terminada'] },
    archivar: { to: 'archivada', from: ['cancelada', 'terminada'] },
};

/**
 * @typedef {{ changed: boolean }} TransitionResult
 */

/**
 * Aplica una transición de estado (`terminar` | `cancelar` | `archivar`)
 * delegando al repositorio la actualización condicional (solo escribe si la
 * fila sigue en uno de los estados `from` de `ACTIONS[action]`).
 *
 * `changed: false` significa que la reserva ya no estaba en un estado `from`
 * válido — otra sesión de staff ya la transicionó, o la transición ya no era
 * legal (0 filas afectadas) — NO es un error; el llamador (wiring) debe
 * mostrar un Toast informativo y re-consultar el estado actual de la fila
 * (Req: Staff Status Transition Actions, escenario "Concurrent transition is
 * rejected"). Un error real del repositorio (Supabase no configurado,
 * `{error}`) se propaga sin capturar.
 *
 * @param {string} id
 * @param {ReservationAction} action
 * @returns {Promise<TransitionResult>}
 * @throws {Error} si `action` no es una de las 3 acciones conocidas, si el
 *   repositorio falla, o si Supabase no está configurado
 */
export async function transition(id, action) {
    const config = ACTIONS[action];
    if (!config) throw new Error(`Acción de reserva desconocida: ${action}`);

    const { affected } = await repoUpdateStatus(id, { to: config.to, from: config.from });
    return { changed: affected };
}

/** Fallback en español cuando el endpoint no devuelve un `error` legible. */
const CREATE_ERROR_FALLBACK = 'No pudimos crear la reserva. Probá de nuevo.';

/**
 * @typedef {{ ok: boolean, error?: string }} CreateReservationResult
 */

/**
 * Crea una reserva desde el panel de staff ("libre + chequea cupo"): POSTea al
 * endpoint `POST /api/reservas` (que usa el service_role y ejecuta la RPC
 * `create_reservation`, la cual garantiza el cupo atómicamente server-side). El
 * navegador NO puede llamar a la RPC directamente — de ahí el POST al endpoint.
 *
 * Normaliza la respuesta HTTP: 200 → `{ ok: true }`; cualquier otro status →
 * `{ ok: false, error }` con el string `error` en español que devuelve el
 * endpoint (p. ej. "Sin cupo. Quedan N lugares.") o un fallback. Un fallo real
 * de red (timeout/offline) se propaga como throw para que el wiring lo capture
 * y muestre un Toast genérico — mismo criterio que `createBlock`.
 *
 * @param {{ nombre: string, apellido: string, telefono?: string|null, fecha: string, momento: string, hora?: string|null, personas: number|string, area: string }} payload
 * @returns {Promise<CreateReservationResult>}
 * @throws {Error} si el fetch falla (red/timeout) — no se captura acá.
 */
export async function createReservation(payload) {
    const response = await safeFetchClient(CONFIG.API.RESERVAS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            nombre: payload.nombre,
            apellido: payload.apellido,
            telefono: payload.telefono ?? null,
            fecha: payload.fecha,
            momento: payload.momento,
            hora: payload.hora ?? null,
            personas: payload.personas,
            area: payload.area,
            comentarios: payload.comentarios ?? null,
        }),
    });

    const data = await response.json().catch(() => null);

    // El id de la reserva creada lo devuelve el endpoint (`reservation_id`) y
    // hasta ahora se descartaba. Lo necesita el alta con mesas: las mesas
    // elegidas en el formulario solo se pueden asignar DESPUÉS de que la fila
    // exista, y sin el id no hay contra qué asignarlas.
    if (response.ok) return { ok: true, reservationId: data?.reservation_id ?? null };

    return { ok: false, error: data?.error ?? CREATE_ERROR_FALLBACK };
}

/** Campos que el formulario de edición puede tocar. `status` NO es uno de ellos. */
const EDITABLE_FIELDS = ['nombre', 'apellido', 'telefono', 'fecha', 'momento', 'hora', 'personas', 'area', 'comentarios'];

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Valida y aplica una edición de reserva.
 *
 * Repite los limites del alta (obligatorios, tamano de grupo, formato de fecha)
 * porque la edición NO pasa por `api/reservas.js`: escribe directo por RLS,
 * igual que `updateStatus`. Sin esta validación, editar sería un camino más
 * permisivo que crear para los mismos datos.
 *
 * @param {string} id
 * @param {object} input campos del formulario
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function editReservation(id, input) {
    const fields = {};
    for (const key of EDITABLE_FIELDS) {
        if (input[key] !== undefined) fields[key] = input[key];
    }

    if (!fields.nombre || !fields.apellido || !fields.fecha || !fields.momento || !fields.area) {
        return { ok: false, error: 'Completá todos los campos.' };
    }
    if (!FECHA_RE.test(fields.fecha)) {
        return { ok: false, error: 'Fecha inválida.' };
    }
    const personas = Number(fields.personas);
    if (!Number.isInteger(personas) || personas < PERSONAS_MIN || personas > PERSONAS_MAX) {
        return { ok: false, error: `Cantidad de personas inválida (máx ${PERSONAS_MAX}).` };
    }
    fields.personas = personas;

    try {
        await repoUpdateReservation(id, fields);
        return { ok: true };
    } catch (error) {
        // Mover la reserva a un turno donde alguna de sus mesas ya está tomada
        // aborta el UPDATE completo (unique index de doble booking).
        if (error?.code === '23505') {
            return {
                ok: false,
                error: 'No pudimos mover la reserva: alguna de sus mesas ya está tomada en el turno destino. Liberala primero.',
            };
        }
        return { ok: false, error: 'No pudimos guardar los cambios. Probá de nuevo.' };
    }
}
